/**
 * Column Width Utilities
 * Contains width calculation utilities and helper functions for table columns
 * 
 * Custom Formatting Examples:
 * 
 * 1. Currency formatting:
 *    {
 *      accessorKey: "salary",
 *      inputType: "number",
 *      numberFormat: { style: 'currency', currency: 'EUR' }
 *    }
 * 
 * 2. Custom date format:
 *    {
 *      accessorKey: "birthdate",
 *      inputType: "date",
 *      dateFormat: { year: 'numeric', month: 'long', day: 'numeric' }
 *    }
 * 
 * 3. Percentage:
 *    {
 *      accessorKey: "completion_rate",
 *      inputType: "number",
 *      numberFormat: { style: 'percent', minimumFractionDigits: 1 }
 *    }
 * 
 * 4. Custom formatter:
 *    {
 *      accessorKey: "status",
 *      formatter: (value) => value === 'active' ? '✓ Active' : '✗ Inactive'
 *    }
 * 
 * 5. Age calculation from date:
 *    {
 *      accessorKey: "birthdate",
 *      inputType: "date",
 *      formatter: (value) => {
 *        if (!value) return '';
 *        const age = new Date().getFullYear() - new Date(value).getFullYear();
 *        return `${formatDate(value)} (${age} years)`;
 *      }
 *    }
 */

import React from "react";
import type { EditableCellConfig } from "@/components/table/types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function calculateColumnWidth<T extends Record<string, any>>(
  config: EditableCellConfig<T>,
  data: T[]
): number {
  if (config.width) return config.width;

  if (config.accessorKey === "id" && config.cell) {
    return 140;
  }

  const MIN_WIDTHS: Record<string, number> = {
    date: 140,
    email: 120,
    select: 150,
    "enhanced-select": 150,
    text: 120,
    number: 100,
    badge: 180, // Minimum width for badge/label columns
  };

  let maxContentLength = 0;
  const headerLength = typeof config.header === "string"
    ? config.header.length
    : React.isValidElement(config.header)
    ? (() => {
        try {
          const element = config.header as React.ReactElement<{ children?: React.ReactNode }>;
          const children = element.props?.children;
          if (typeof children === "string") {
            return children.length;
          }
          if (React.isValidElement(children)) {
            return children.toString().length;
          }
          return 0;
        } catch {
          return 0;
        }
      })()
    : 0;
  maxContentLength = Math.max(maxContentLength, headerLength);

  data.forEach((row) => {
    const value = row[config.accessorKey];
    if (value != null) {
      let displayLength = 0;
      if ((config.inputType === "select" || config.inputType === "enhanced-select") && config.selectOptions) {
        const option = config.selectOptions.find((opt) => opt.value === value);
        displayLength = option ? option.label.length : String(value).length;
      } else if (config.inputType === "date") {
        displayLength = 10;
      } else if (
        typeof config.accessorKey === "string" &&
        (config.accessorKey.includes("At") || config.accessorKey.includes("Time"))
      ) {
        displayLength = 16;
      } else if (Object.prototype.toString.call(value) === '[object Date]') {
        displayLength = 10;
      } else if (Array.isArray(value) && value.length > 0 && value[0]?.label) {
        // Handle badge/label arrays (e.g., group labels)
        const badges = value as Array<{ label: string }>;
        const badgeWidths = badges.map(badge => {
          const labelLength = badge.label?.length || 0;
          // Badge overhead: padding (16px) + border (2px) + margin (4px)
          const badgeOverhead = 22;
          return labelLength * 8 + badgeOverhead; // 8px per char for badges
        });
        
        // Calculate total width for badges on a single row
        const totalBadgeWidth = badgeWidths.reduce((sum, width) => sum + width, 0);
        const gapWidth = (badges.length - 1) * 4; // 4px gap between badges
        const totalWidth = totalBadgeWidth + gapWidth;
        
        // Convert to character equivalent for consistent calculation
        displayLength = Math.ceil(totalWidth / 9); // 9px standard char width
        
        // Set reasonable bounds: minimum 2 badges worth, maximum 4 badges worth
        const minBadgeDisplay = Math.ceil((2 * 60) / 9); // ~13 chars
        const maxBadgeDisplay = Math.ceil((4 * 80) / 9); // ~36 chars
        displayLength = Math.max(minBadgeDisplay, Math.min(maxBadgeDisplay, displayLength));
      } else {
        displayLength = String(value).length;
      }
      maxContentLength = Math.max(maxContentLength, displayLength);
    }
  });

  const basePadding = config.editable === false ? 40 : 60;
  const standardCharWidth = 9;
  
  // Detect if this is a badge column by checking if any data contains badge arrays
  const isBadgeColumn = data.some(row => {
    const value = row[config.accessorKey];
    return Array.isArray(value) && value.length > 0 && value[0]?.label;
  });
  
  let estimatedWidth = Math.max(
    maxContentLength * standardCharWidth + basePadding,
    MIN_WIDTHS[config.inputType || (isBadgeColumn ? "badge" : "text")] || 120
  );

  if (config.inputType === "email") {
    const emailCharWidth = 8;
    const emailBasePadding = 30;
    const emailEstimatedWidth = maxContentLength * emailCharWidth + emailBasePadding;
    estimatedWidth = Math.max(emailEstimatedWidth, MIN_WIDTHS.email || 120);
  }

  if (config.editable !== false) {
    return estimatedWidth + 10;
  }

  return estimatedWidth;
}